import { useCallback, useSyncExternalStore } from "react";
import { useSyncEngine } from "@/sync/context";
import type { ModelName, RecordOf } from "@/sync/types";

/**
 * Number of records in the local pool matching `predicate`. Re-renders only when the count
 * changes, so it is cheap enough for always-mounted UI like the sidebar. No network: lazy models
 * count whatever partials are already loaded.
 */
export function useLocalCount<N extends ModelName>(
	model: N,
	predicate: (record: RecordOf<N>) => boolean,
) {
	const { pool } = useSyncEngine();
	const subscribe = useCallback(
		(listener: () => void) => pool.subscribeModel(model, listener),
		[pool, model],
	);
	return useSyncExternalStore(subscribe, () => {
		let count = 0;
		for (const record of pool.list(model)) if (predicate(record as unknown as RecordOf<N>)) count++;
		return count;
	});
}

/**
 * Inbox work from synced data: chat proposals awaiting approval, posts pending approval and
 * unanswered comments from other people. Flow-run approvals are REST-only and not included.
 */
export function useLocalInboxCounts(projectId: string | undefined) {
	const proposals = useLocalCount(
		"assistantToolCall",
		(c) => c.projectId === projectId && c.status === "proposed",
	);
	const posts = useLocalCount(
		"scheduledPost",
		(p) => p.projectId === projectId && p.status === "pending_approval",
	);
	const comments = useLocalCount(
		"socialComment",
		(c) => c.projectId === projectId && c.status === "new" && !c.isOwner && !c.deletedAt,
	);
	return { approvals: proposals + posts, comments };
}
